<template>
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5">
      <TableTitle
        :title="title"
        :pageTitle="pageTitle"
        icon="fas fa-users"
        :showAction="true"
        actionLabel="Create User"
        :modal="'createNew'"
        :disabled="!canCreate('settings')"
      />
      <CommonTable2
        :tableObj="tableConfig"
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        permissionKey="settings"
      />
    </div>
  </div>

  <!-- pagination -->
  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <div class="pagination-mini">
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
      >
        {{ p }}
      </button>

      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <!-- Create Modal -->
  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    saveButtonText="Create"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";
const { canCreate } = usePermissions();
import apiService from "@/services/apiService";

const API_RESOURCE = "/cms-users";
const ROLES_API = "/cms-roles-permissions/roles";
const pageTitle = "CMS User";
const title = "CMS Users Management";

// Use the generic CRUD composable
const crud = useCrud(API_RESOURCE);

// Roles options for form
const roles = ref<any[]>([]);

// pagination local UI state
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

// visiblePages computed
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Table configuration
const rows = ref<any[]>([]);
const tableConfig = ref({
  columns: [
    { name: "Name", field: "name", sortable: true },
    { name: "Email", field: "email", sortable: true },
    { name: "Role", field: "role_name", sortable: true },
    { name: "Created At", field: "created_at", sortable: true },
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  showStatusFilter: false,
  table_width: "max-content",
});

watch(crud.data, (newVal) => {
  rows.value = (newVal || []).map((user: any) => ({
    ...user,
    role_name: user.role_id?.title || "N/A",
  }));
});

watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

// Sync backend pagination
watch(
  () => crud.pagination.value,
  (newVal) => {
    if (!newVal) return;
    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next =
      tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// === ACTIONS ===
const changeRows = async () => {
  currentPage.value = 1;
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  currentPage.value = page;
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({
  name: "",
  email: "",
  password: "",
  role_id: "",
  profile_pic: "",
  is_blocked: false,
  allow_otp_login: true,
});

const editingItemData = ref<any>({
  _id: "",
  name: "",
  email: "",
  role_id: "",
  profile_pic: "",
  is_blocked: false,
  allow_otp_login: true,
});

// --- Form Definitions ---
const createFormFields = computed<FormField[]>(() => [
  {
    key: "name",
    label: "Name",
    type: "text",
    required: true,
    placeholder: "Enter full name",
  },
  {
    key: "email",
    label: "Email",
    type: "email",
    required: true,
    placeholder: "Enter email address",
  },
  {
    key: "password",
    label: "Password",
    type: "text",
    required: true,
    placeholder: "Enter password",
  },
  {
    key: "role_id",
    label: "Role",
    type: "select",
    required: true,
    options: roles.value,
  },
  {
    key: "profile_pic",
    label: "Profile Picture URL",
    type: "text",
    required: false,
    placeholder: "Enter profile picture URL",
  },
  {
    key: "is_blocked",
    label: "Block User Account",
    type: "switch",
    required: false,
  },
  {
    key: "allow_otp_login",
    label: "Allow OTP Login",
    type: "switch",
    required: false,
  },
]);

const editFormFields = computed<FormField[]>(() => [
  {
    key: "name",
    label: "Name",
    type: "text",
    required: true,
    placeholder: "Enter full name",
    readonly: isViewMode.value,
  },
  {
    key: "email",
    label: "Email",
    type: "email",
    required: true,
    placeholder: "Enter email address",
    readonly: true, // Email usually read-only during edit
  },
  {
    key: "role_id",
    label: "Role",
    type: "select",
    required: true,
    options: roles.value,
    readonly: isViewMode.value,
  },
  {
    key: "profile_pic",
    label: "Profile Picture URL",
    type: "text",
    required: false,
    placeholder: "Enter profile picture URL",
    readonly: isViewMode.value,
  },
  {
    key: "is_blocked",
    label: "Block User Account",
    type: "switch",
    required: false,
    readonly: isViewMode.value,
  },
  {
    key: "allow_otp_login",
    label: "Allow OTP Login",
    type: "switch",
    required: false,
    readonly: isViewMode.value,
  },
]);

// --- Handlers ---
const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { 
    ...row,
    role_id: row.role_id?._id || row.role_id
  };
  isEditModalOpen.value = true;
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { 
    ...row,
    role_id: row.role_id?._id || row.role_id
  };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this user?")) {
    await crud.deleteItem(id);
    await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const handleSaveCreate = async () => {
  const result = await crud.createItem(newItemData.value);
  if (result) {
    closeCreateModal();
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, role_name, created_at, updated_at, deleted_at, ...updatePayload } = editingItemData.value;
  
  // Convert role_id if it's an object
  if (typeof updatePayload.role_id === 'object') {
    updatePayload.role_id = updatePayload.role_id._id;
  }

  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
    await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = {
    name: "",
    email: "",
    password: "",
    role_id: "",
    profile_pic: "",
    is_blocked: false,
    allow_otp_login: true,
  };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
};

const fetchRoles = async () => {
  try {
    const response = await apiService.get(ROLES_API);
    roles.value = response.data || response;
  } catch (error) {
    console.error("Failed to fetch roles:", error);
  }
};

// --- lifecycle ---
onMounted(async () => {
  await fetchRoles();
  await crud.fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
  });
});
</script>

<style scoped>
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}
.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child { border-top-left-radius: 6px; border-bottom-left-radius: 6px; }
.page-btn:last-child { border-top-right-radius: 6px; border-bottom-right-radius: 6px; }
.page-btn:hover { background: #e9ecef; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-active { background: #21c1d6; color: #fff; border: 1px solid #21c1d6; font-weight: 600; }
.prev_btn, .next_btn { color: #21c1d6; }
</style>